import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../api';
import ReceiptDataPanel, { ReceiptData } from './shared/ReceiptDataPanel';

interface ReceiptAnalysisTabProps {
  preloadedReceiptId?: number | null;
  onPreloadConsumed?: () => void;
}

const ReceiptAnalysisTab: React.FC<ReceiptAnalysisTabProps> = ({ preloadedReceiptId, onPreloadConsumed }) => {
  const [data, setData] = useState<ReceiptData | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(false);
  const prevObjectUrl = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [tags, setTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  useEffect(() => {
    api.get('/tags').then((r) => setAvailableTags(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (preloadedReceiptId != null) {
      loadExistingReceipt(preloadedReceiptId);
      onPreloadConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadedReceiptId]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setLoading(true);
    setError(null);
    setData(null);
    setSelectedFile(file);
    setIsImage(!file.type.includes('pdf'));
    const objectUrl = URL.createObjectURL(file);
    if (prevObjectUrl.current) URL.revokeObjectURL(prevObjectUrl.current);
    prevObjectUrl.current = objectUrl;
    setSourceUrl(objectUrl);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', 'receipt');
    try {
      const response = await api.post('/extract', formData);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.status === 422
        ? `Kein gültiger Beleg: ${err.response.data.detail}`
        : 'Extraktion fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExistingReceipt = async (id: number) => {
    setLoading(true);
    try {
      const response = await api.get(`/receipt/${id}`);
      const receipt = response.data;
      setData(receipt.data);
      setTags(receipt.tags || []);
      const ext = receipt.minio_path?.split('.').pop()?.toLowerCase();
      setIsImage(ext !== 'pdf');
      if (prevObjectUrl.current) { URL.revokeObjectURL(prevObjectUrl.current); prevObjectUrl.current = null; }
      const fileRes = await api.get(`/get_file?path=${encodeURIComponent(receipt.minio_path)}`, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(fileRes.data);
      prevObjectUrl.current = objectUrl;
      setSourceUrl(objectUrl);
    } catch (err) {
      setError('Fehler beim Laden des bestehenden Dokuments.');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreInDMS = async () => {
    if (!data || !selectedFile) { alert("Keine Daten oder Datei zum Speichern vorhanden."); return; }
    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('data_json', JSON.stringify(data));
    formData.append('tags_json', JSON.stringify(tags));
    formData.append('reviewed', 'true');
    try {
      await api.post('/store_dms', formData);
      alert("Erfolgreich im DMS gespeichert und indexiert.");
    } catch (error: any) {
      if (error.response?.status === 409) {
        const { message, existing_id } = error.response.data.detail;
        if (window.confirm(`${message}\nMöchten Sie das vorhandene Dokument öffnen?`)) {
          await loadExistingReceipt(existing_id);
        }
      } else {
        setError('Speichern im DMS fehlgeschlagen.');
      }
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'], 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] }
  });

  return (
    <div className={`w-full transition-all duration-500 ${data ? 'max-w-6xl' : 'max-w-xl'}`}>
      <div
        {...getRootProps()}
        className={`w-full p-10 border-2 border-dashed rounded-lg cursor-pointer transition-colors
          ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-blue-400'}`}
      >
        <input {...getInputProps()} />
        <div className="text-center text-gray-500">
          {loading ? (
            <p className="animate-pulse font-semibold">Dokument wird verarbeitet...</p>
          ) : isDragActive ? (
            <p>PDF hier ablegen...</p>
          ) : (
            <p>Ziehen Sie eine PDF-Rechnung hierher oder klicken Sie zum Auswählen</p>
          )}
        </div>
      </div>

      {error && <div className="mt-4 p-4 text-red-700 bg-red-100 rounded w-full">{error}</div>}

      {data && (
        <div className="mt-8 flex flex-col lg:flex-row gap-6 items-start">
          {/* Vorschau */}
          <div className="w-full lg:w-1/2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden flex flex-col h-[600px]">
            <div className="bg-gray-100 px-4 py-3 border-b text-sm font-semibold text-gray-600">
              Rechnungs-Original
            </div>
            <div className="flex-1 overflow-hidden p-4 bg-gray-50 relative group">
              {sourceUrl && (isImage ? (
                <div className="w-full h-full flex flex-col">
                  <div className="absolute top-6 right-6 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setZoom(z => Math.max(z - 0.2, 0.5))} className="bg-white border shadow-sm px-3 py-1 rounded-l hover:bg-gray-50 text-gray-700 font-bold">-</button>
                    <button onClick={() => setZoom(1)} className="bg-white border-y shadow-sm px-2 py-1 hover:bg-gray-50 text-xs text-gray-600 min-w-[50px]">{Math.round(zoom * 100)}%</button>
                    <button onClick={() => setZoom(z => Math.min(z + 0.2, 4))} className="bg-white border shadow-sm px-3 py-1 rounded-r hover:bg-gray-50 text-gray-700 font-bold">+</button>
                  </div>
                  <div className="flex-1 overflow-auto flex items-center justify-center">
                    <img src={sourceUrl} style={{ transform: `scale(${zoom})`, transition: 'transform 0.15s ease-out' }} className="max-w-full max-h-full object-contain shadow-md border rounded origin-center" alt="Beleg Vorschau" />
                  </div>
                </div>
              ) : (
                <iframe src={sourceUrl} className="w-full h-full border rounded shadow-inner" title="Preview" />
              ))}
            </div>
          </div>

          {/* Daten-Panel */}
          <div className="w-full lg:w-1/2">
            <ReceiptDataPanel
              data={data}
              tags={tags}
              availableTags={availableTags}
              onChange={setData}
              onTagsChange={setTags}
              headerTitle="Extrahierte Daten"
              headerColor="bg-blue-600"
              footer={
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                  onClick={handleStoreInDMS}
                  disabled={loading}
                >
                  {loading ? 'Speichere...' : 'Daten in DMS übernehmen'}
                </button>
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptAnalysisTab;
